// Prakrit / Sanskrit: members with access to both courses (both plans, Sanskrit + Prakrit, the trial or staff) switch
// between them here. Shown in the Prakrit settings popover and sheet, the Sanskrit settings sheet and the account page;
// renders nothing for everyone else.

import type { FC, ReactNode } from 'react'
import type { Course } from '@/lib/plans'

import Image from 'next/image'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { useSession } from '@/lib/client/session'
import { COURSE_HOME } from '@/lib/plans'
import { COURSE_LOGO, COURSE_NAME } from '@/components/app/site-header'

const COURSES: Course[] = ['prakrit', 'sanskrit']

/** The course of the current page: /sanskrit/* is Sanskrit, everything else Prakrit. */
export const courseOfPath = (pathname: string): Course => (pathname === '/sanskrit' || pathname.startsWith('/sanskrit/') ? 'sanskrit' : 'prakrit')

export const CourseSwitch: FC<{ className?: string, onSwitch?: () => void }> = ({ className, onSwitch }): ReactNode => {
    const router = useRouter()
    const { user, access, loading } = useSession()

    if (loading || !user || !access.prakrit || !access.sanskrit) return null

    const current = courseOfPath(router.pathname)

    return (
        <fieldset className={cn('flex flex-col gap-2', className)}>
            <legend className='mb-2 text-sm font-semibold'>Course</legend>
            <div className='grid grid-cols-2 gap-1 rounded-xl bg-secondary p-1'>
                {COURSES.map(course => (
                    <button
                        key={course}
                        type='button'
                        aria-pressed={current === course}
                        onClick={() => {
                            onSwitch?.()
                            if (current !== course) void router.push(COURSE_HOME[course])
                        }}
                        className={cn(
                            'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-2 text-sm font-semibold transition-colors',
                            current === course ? 'bg-card text-foreground shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Image src={COURSE_LOGO[course]} alt='' width={24} height={24} className='size-6 rounded-md bg-white object-cover' />
                        {COURSE_NAME[course]}
                    </button>
                ))}
            </div>
            <p className='text-xs text-muted-foreground'>Your Premium covers both courses. Progress in each one is kept.</p>
        </fieldset>
    )
}
